import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * The reasons the server accepts (`chat_reports_reason_check`). Kept as a
 * literal list rather than derived from a type so the copy shown to the reporter
 * lives next to the value it sends.
 */
const REPORT_REASONS = [
  { value: 'spam', label: 'Spam', description: 'Unsolicited or repetitive messages' },
  { value: 'harassment', label: 'Harassment', description: 'Targeted abuse or bullying' },
  { value: 'hate_speech', label: 'Hate speech', description: 'Attacks on a person or group' },
  {
    value: 'inappropriate',
    label: 'Inappropriate content',
    description: 'Sexual, graphic, or otherwise unsuitable content',
  },
  { value: 'other', label: 'Something else', description: 'Describe it below' },
] as const;

/** Mirrors the route's own cap so an over-long note fails here, not at the API. */
const MAX_DESCRIPTION = 2000;

interface ReportUserDialogProps {
  userId: string;
  displayName: string;
  /**
   * Files the report against a specific message as well as the person. The
   * moderator sees both, which is the difference between "this account is a
   * problem" and "here is the message that proves it".
   */
  messageId?: string;
  /** The channel the report was raised from, when there is one. */
  channelId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReportUserDialog({
  userId,
  displayName,
  messageId,
  channelId,
  open,
  onOpenChange,
}: ReportUserDialogProps) {
  const [reason, setReason] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // A reopened dialog must start clean — carrying the previous reason over is
  // how someone files a report they didn't mean to.
  useEffect(() => {
    if (open) {
      setReason(null);
      setDescription('');
      setError(null);
      setSubmitted(false);
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!reason) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.reportChatMessage({
        reportedUserId: userId,
        messageId,
        channelId,
        reason,
        description: description.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not file that report. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{submitted ? 'Report received' : `Report ${displayName}`}</DialogTitle>
          {/* RECORDED, not REVIEWED — the distinction is deliberate. Filing a
              report writes a `chat_reports` row and nothing else: there is no
              triage UI, no ticket, no notification (see the block comment on
              POST /reports in routes/arches/chat/moderation.ts). Do not promise
              review here until that surface exists. */}
          <DialogDescription>
            {submitted
              ? 'Thanks — this has been recorded for the Fias moderation team. You will not be notified of the outcome.'
              : 'Your report is recorded for the Fias moderation team. The person you report is not told who reported them.'}
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          </DialogFooter>
        ) : (
          <>
            <div className="space-y-1">
              <Label className="text-sm font-medium">Reason</Label>
              <div className="flex flex-col gap-1 mt-2">
                {REPORT_REASONS.map(({ value, label, description: desc }) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={reason === value}
                    onClick={() => setReason(value)}
                    className={cn(
                      'flex items-start gap-3 rounded-lg px-3 py-2 text-left transition-colors',
                      reason === value
                        ? 'bg-primary/10 border border-primary/30'
                        : 'border border-transparent hover:bg-muted',
                    )}
                  >
                    <span
                      className={cn(
                        'mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center',
                        reason === value ? 'border-primary bg-primary' : 'border-muted-foreground',
                      )}
                    >
                      {reason === value && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </span>
                    <div>
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground">{desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="report-description">What happened? (optional)</Label>
              <Textarea
                id="report-description"
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION))}
                placeholder="Add anything that would help a moderator understand."
                maxLength={MAX_DESCRIPTION}
                rows={3}
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleSubmit} disabled={!reason || submitting}>
                {submitting ? 'Sending…' : 'Submit report'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
